"use client";

import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactAppointment() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="contact-appointment" ref={sectionRef} className={`bg-card text-foreground py-14 md:py-20 lg:py-24 transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Action side */}
          <div className="lg:col-span-7">
            <h2 className="font-serif font-normal text-3xl md:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-foreground border-l-4 border-primary pl-4 mb-4">
              Schedule your project
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed pl-5 mb-8">
              Choose an appointment slot online or call Prince directly to talk through your project needs, scope, and timeline.
            </p>

            <div className="border border-border rounded bg-background p-6 md:p-8 space-y-6">
              <div>
                <h3 className="font-serif font-normal text-xl text-foreground mb-2">
                  Book an on-site visit
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  Select your service, choose a preferred date and time, and submit your project details. We will confirm your appointment promptly.
                </p>
                <Button asChild className="rounded h-12 px-6 font-medium text-sm w-full sm:w-auto">
                  <Link to="/BookAppointmentPage">
                    <span>Book an appointment</span>
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>

              <div className="h-px bg-border w-full" />

              <div>
                <h3 className="font-serif font-normal text-xl text-foreground mb-2">
                  Call for immediate questions
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Speak directly with Prince Owusu for active jobs, emergency repairs, or fast scoping questions.
                </p>
                <a href="tel:+15712496482" className="inline-flex items-center gap-3 text-lg font-mono text-primary hover:underline">
                  <Phone className="size-5 shrink-0" />
                  <span>+1 (571) 249-6482</span>
                </a>
              </div>
            </div>
          </div>

          {/* Details / Specs side */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="border border-border rounded bg-background p-6 md:p-8">
              <h3 className="font-serif font-normal text-xl text-foreground mb-6 pb-3 border-b border-border">
                Direct contact details
              </h3>

              <ul className="space-y-5">
                <li className="flex items-start gap-3">
                  <Phone className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                      Phone
                    </div>
                    <a href="tel:+15712496482" className="text-base text-foreground font-mono hover:text-primary transition-colors">
                      +1 (571) 249-6482
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <Mail className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                      Email
                    </div>
                    <a href="mailto:princeowusu989@yahoo.com" className="text-base text-foreground font-mono hover:text-primary transition-colors break-all">
                      princeowusu989@yahoo.com
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                      Address
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">
                      17669 Avenel Lane, Dumfries, Virginia 22026
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Serving Dumfries, Virginia and the surrounding area
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <Clock className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                      Working hours
                    </div>
                    <p className="text-sm text-foreground">
                      Mon-Sat, 7am - 7pm
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}